import React, { useEffect, useContext, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import CustomDialog from '../components/organisms/CustomDialog'
import GoogleMap from '../components/molecules/GoogleMap/GoogleMap'
import { useFormik } from 'formik'
import { mapData } from '@/product/common/components'
import PrimaryButton from '@/product/components/atoms/PrimaryButton'
import { handleRedirectInternal } from './components'
import AuthContext from '@/product/context/auth/authContext'
import UserContext from '@/product/context/user/userContext'
import { log } from '@craco/craco/lib/logger'

const LocationSelector = (props) => {
    const authContext = useContext(AuthContext)
    const userContext = useContext(UserContext)
    const { user, isAuthenticated, loadUser } = authContext
    const location = useLocation()
    const [changed, setChanged] = useState(false)
    const [authChanged, setAuthChanged] = useState(false)

    const { updateProfile, responseStatus, clearResponse } = userContext

    const history = useHistory()

    const initialGeoLocation = JSON.parse(localStorage.getItem('geoLocation')) || {}

    const defaultLocation = {
        address1: 'USA',
        city: '',
        state: '',
        zip: '',
        country: global.defaultCountry || 'US',
        full_address: '',
        place_id: 'ChIJCzYy51S161QRQrfeQ5K5Oxw',
        latitude: '39.8283',
        longitude: '-98.5795',
        radius: 1500,
    }

    const formik = useFormik({
        initialValues: {
            address1: initialGeoLocation.locationName || defaultLocation.address1,
            city: initialGeoLocation.city || '',
            state: initialGeoLocation.state || '',
            zip: initialGeoLocation.zip || '',
            country: initialGeoLocation.country || global.defaultCountry || '',
            full_address: initialGeoLocation.full_address || '',
            place_id: initialGeoLocation.place_id || '',
            latitude: initialGeoLocation.latitude || defaultLocation.latitude,
            longitude: initialGeoLocation.longitude || defaultLocation.longitude,
            radius: defaultLocation.radius,
        },
        onSubmit: (values) => {
            if (user && user?.id) {
                const locationToSave = { ...values }
                // delete locationToSave.radius
                let stringifyLocation = JSON.stringify(locationToSave)
                localStorage.setItem('upDatedLocation', stringifyLocation)
                updateProfile(
                    { id: user.id, user_location: stringifyLocation },
                    true,
                    'locationUpdate',
                )

                if (values.city) {
                    props.setCityName([values.city, values.state].filter(Boolean).join(', '))
                    props.setLocationName('')
                } else {
                    props.setLocationName(values.address1)
                    props.setCityName('')
                }
            } else {
                const params = `longitude=${values.longitude}&latitude=${values.latitude}&radius=${
                    parseFloat(values.radius || 10) * 1609.34
                }`
                if (location.pathname !== '/search') {
                    handleRedirectInternal(history, `?${params}`)
                } else {
                    handleRedirectInternal(history, `search?${params}`)
                }
                if (values.city) {
                    props.setLocationName('')
                    props.setCityName([values.city, values.state].filter(Boolean).join(', '))
                } else {
                    props.setLocationName(values.address1)
                    props.setCityName('')
                }
                props.handleLocation()
            }
        },
    })

    useEffect(() => {
        if (user?.id && user?.user_location) {
            const _userLocation = JSON.parse(user.user_location) || {}
            if (_userLocation?.address1) {
                formik.setFieldValue(
                    'address1',
                    _userLocation?.address1 ||
                        initialGeoLocation.locationName ||
                        defaultLocation.address1,
                )
                formik.setFieldValue('city', _userLocation?.city || initialGeoLocation.city || '')
                formik.setFieldValue(
                    'state',
                    _userLocation?.state || initialGeoLocation.state || '',
                )
                formik.setFieldValue('zip', _userLocation?.zip || initialGeoLocation.zip || '')
                formik.setFieldValue(
                    'country',
                    _userLocation?.country ||
                        initialGeoLocation.country ||
                        global.defaultCountry ||
                        '',
                )
                formik.setFieldValue(
                    'full_address',
                    _userLocation?.full_address || initialGeoLocation.full_address || '',
                )
                formik.setFieldValue(
                    'place_id',
                    _userLocation?.place_id || initialGeoLocation.place_id || '',
                )
                formik.setFieldValue(
                    'latitude',
                    _userLocation?.latitude ||
                        initialGeoLocation.latitude ||
                        defaultLocation.latitude,
                )
                formik.setFieldValue(
                    'longitude',
                    _userLocation?.longitude ||
                        initialGeoLocation.longitude ||
                        defaultLocation.longitude,
                )
                formik.setFieldValue('radius', _userLocation?.radius || defaultLocation.radius)
            }
        }
    }, [user?.user_location])

    useEffect(() => {
        const handleGeoLocationReady = () => {
            const newGeoLocation = JSON.parse(localStorage.getItem('geoLocation')) || {}

            if (
                formik.values.address1 === defaultLocation.address1 ||
                formik.values.address1 === initialGeoLocation.locationName
            ) {
                formik.setFieldValue(
                    'address1',
                    newGeoLocation.locationName || defaultLocation.address1,
                )
            }
            if (
                formik.values.latitude === defaultLocation.latitude ||
                formik.values.latitude === initialGeoLocation.latitude
            ) {
                formik.setFieldValue(
                    'latitude',
                    newGeoLocation.latitude || defaultLocation.latitude,
                )
            }
            if (
                formik.values.longitude === defaultLocation.longitude ||
                formik.values.longitude === initialGeoLocation.longitude
            ) {
                formik.setFieldValue(
                    'longitude',
                    newGeoLocation.longitude || defaultLocation.longitude,
                )
            }
            formik.setFieldValue('city', newGeoLocation.city || '')
            formik.setFieldValue('state', newGeoLocation.state || '')
            formik.setFieldValue('zip', newGeoLocation.zip || '')
            formik.setFieldValue('country', newGeoLocation.country || global.defaultCountry || '')
            formik.setFieldValue('place_id', newGeoLocation.place_id || '')
            formik.setFieldValue('full_address', newGeoLocation.full_address || '')

            if (!user?.user_location) {
                if (newGeoLocation.city && props?.setCityName) {
                    props.setLocationName('')
                    props.setCityName(
                        [newGeoLocation.city, newGeoLocation.state].filter(Boolean).join(', '),
                    )
                } else if (newGeoLocation.locationName) {
                    props.setLocationName(newGeoLocation.locationName)
                    props.setCityName('')
                } else {
                    props.setLocationName(defaultLocation.address1)
                    props.setCityName('')
                }
            }
        }

        window.addEventListener('geoLocationReady', handleGeoLocationReady)

        return () => {
            window.removeEventListener('geoLocationReady', handleGeoLocationReady)
        }
    }, [props.setCityName, props.setLocationName, defaultLocation, user?.user_location])

    useEffect(() => {
        const newGeoLocation2 = JSON.parse(localStorage.getItem('geoLocation')) || {}
        if (localStorage.getItem('clearLocation') == '1' && Object.keys(newGeoLocation2).length) {
            if (newGeoLocation2.city && props?.setCityName) {
                props.setLocationName('')
                props.setCityName(
                    [newGeoLocation2.city, newGeoLocation2.state].filter(Boolean).join(', '),
                )
                formik.setFieldValue(
                    'address1',

                    [newGeoLocation2.city, newGeoLocation2.state].filter(Boolean).join(', '),
                )
            } else if (newGeoLocation2.locationName) {
                props.setLocationName(newGeoLocation2.locationName)
                props.setCityName('')
                formik.setFieldValue(
                    'address1',

                    newGeoLocation2.locationName,
                )
            } else {
                props.setLocationName(newGeoLocation2.address1)
                props.setCityName('')
                formik.setFieldValue(
                    'address1',

                    newGeoLocation2.address1,
                )
            }
            formik.setFieldValue(
                'radius',

                1500,
            )
            localStorage.setItem('clearLocation', '0')
        }
    }, [localStorage.getItem('clearLocation')])

    useEffect(() => {
        const currentGeoLocation = JSON.parse(localStorage.getItem('geoLocation')) || {}
        if (!isAuthenticated) {
            formik.setFieldValue(
                'address1',

                currentGeoLocation.locationName,
            )
            formik.setFieldValue(
                'latitude',

                currentGeoLocation.latitude,
            )
            formik.setFieldValue(
                'longitude',

                currentGeoLocation.longitude,
            )
            formik.setFieldValue('radius', 1500)
            if (currentGeoLocation.city && props?.setCityName) {
                props.setLocationName('')
                props.setCityName(
                    [currentGeoLocation.city, currentGeoLocation.state].filter(Boolean).join(', '),
                )
            } else if (currentGeoLocation.locationName) {
                props.setLocationName(currentGeoLocation.locationName)
                props.setCityName('')
            } else {
                props.setLocationName(defaultLocation.address1)
                props.setCityName('')
            }
        } else {
            const _userLocation = (user?.user_location && JSON.parse(user.user_location)) || {}

            if (_userLocation.city) {
                props.setLocationName('')
                props.setCityName(
                    [_userLocation.city, _userLocation.state].filter(Boolean).join(', '),
                )
            } else if (_userLocation.address1) {
                props.setLocationName(_userLocation.address1)
                props.setCityName('')
            } else if (currentGeoLocation.city) {
                props.setLocationName('')
                props.setCityName(
                    [currentGeoLocation.city, currentGeoLocation.state].filter(Boolean).join(', '),
                )
            } else if (currentGeoLocation.locationName) {
                props.setLocationName(currentGeoLocation.locationName)
                props.setCityName('')
            } else {
                props.setLocationName(defaultLocation.address1)
                props.setCityName('')
            }

            if (
                user?.user_location &&
                location.pathname == '/' &&
                !localStorage.getItem('locationUpdated')
            ) {
                const _userLocation = JSON.parse(user.user_location) || {}
                const currentGeoLocation = JSON.parse(localStorage.getItem('geoLocation')) || {}
                formik.setFieldValue(
                    'latitude',
                    _userLocation?.latitude ||
                        currentGeoLocation.latitude ||
                        defaultLocation.latitude,
                )
                formik.setFieldValue(
                    'longitude',
                    _userLocation?.longitude ||
                        currentGeoLocation.longitude ||
                        defaultLocation.longitude,
                )
                // formik.setFieldValue('radius', 1500)
                localStorage.setItem('locationUpdated', true)
                console.log('9999999999999999999999', _userLocation)
                let params = ''
                if (_userLocation && Object.keys(_userLocation).length) {
                    let stringifyLocation = JSON.stringify(_userLocation)
                    localStorage.setItem('upDatedLocation', stringifyLocation)

                    params = `longitude=${_userLocation?.longitude}&latitude=${
                        _userLocation?.latitude
                    }&radius=${
                        parseFloat(_userLocation?.radius || defaultLocation.longitude) * 1609.34
                    }`
                }

                if (location.pathname !== '/search') {
                    handleRedirectInternal(history, `?${params}`)
                } else {
                    handleRedirectInternal(history, `search?${params}`)
                }
            }
        }
    }, [isAuthenticated, user?.user_location])

    useEffect(() => {
        if (responseStatus?.from === 'locationUpdate' && responseStatus?.status === 'success') {
            loadUser()
            clearResponse()
            const params = `longitude=${formik.values.longitude}&latitude=${
                formik.values.latitude
            }&radius=${parseFloat(formik.values.radius || 10) * 1609.34}`
            if (location.pathname !== '/search') {
                handleRedirectInternal(history, `?${params}`)
            } else {
                handleRedirectInternal(history, `search?${params}`)
            }
            props.handleLocation()
        } else if (
            responseStatus?.from === 'locationUpdate2' &&
            responseStatus?.status === 'success'
        ) {
            loadUser()
            clearResponse()
        }
    }, [responseStatus])

    const resetMiles = () => {
        formik.setFieldValue('address1', defaultLocation.address1)
        formik.setFieldValue('country', defaultLocation.country)
        formik.setFieldValue('state', '')
        formik.setFieldValue('city', '')
        formik.setFieldValue('full_address', defaultLocation.full_address)
        formik.setFieldValue('latitude', defaultLocation.latitude)
        formik.setFieldValue('longitude', defaultLocation.longitude)
        formik.setFieldValue('place_id', defaultLocation.place_id)
        formik.setFieldValue('radius', defaultLocation.radius)
    }

    const handleRadiusChange = (event) => {
        const newRadius = parseInt(event.target.value, 10)
        if (event.target.value == defaultLocation.radius) {
            resetMiles()
        } else {
            formik.setFieldValue('radius', newRadius)
        }
    }
    // useEffect(() => {
    //     const urlParams = new URLSearchParams(location.search)
    //     if (urlParams && props.openLocation) {
    //         if (urlParams.has('latitude') && urlParams.get('latitude')) {
    //             formik.setFieldValue('latitude', urlParams.get('latitude'))
    //             formik.values.latitude = urlParams.get('latitude')
    //         }
    //         if (urlParams.has('longitude') && urlParams.get('longitude')) {
    //             formik.setFieldValue('longitude', urlParams.get('longitude'))
    //             formik.values.longitude = urlParams.get('longitude')
    //         }
    //         if (urlParams.has('radius') && urlParams.get('radius')) {
    //             formik.setFieldValue('radius', urlParams.get('radius') / 1609.34)
    //             formik.values.radius = urlParams.get('radius') / 1609.34

    //             handleRadiusChange({ target: { value: urlParams.get('radius') / 1609.34 } })
    //         }
    //         // setChanged(!changed)
    //     }
    //     // else {
    //     //     handleRadiusChange({ target: { value: 1500 } })
    //     // }
    // }, [props.openLocation])
    const locationSelectorFields = {
        formik: formik,
        data: [
            {
                label: (
                    <span>
                        <span className="material-icons locationChange">location_on</span>Location
                    </span>
                ),
                placeholder: 'Enter the Location',
                type: 'google_place_autocomplete',
                defaultCountry: 'US',
                class: 'col-12',
                name: 'address1',
                placeID: true,
                fullAddress: true,
                stateCityFill: true,
                saveFullAddress: true,
            },
            {
                label: 'Radius (miles)',
                placeholder: 'Select the Radius',
                type: 'select',
                class: 'col-12',
                name: 'radius',
                native: true,
                options: [
                    { value: defaultLocation.radius, show: 'All' },
                    { value: 10, show: '10 miles' },
                    { value: 20, show: '20 miles' },
                    { value: 30, show: '30 miles' },
                    { value: 40, show: '40 miles' },
                    { value: 50, show: '50 miles' },
                    { value: 60, show: '60 miles' },
                    { value: 70, show: '70 miles' },
                    { value: 80, show: '80 miles' },
                    { value: 90, show: '90 miles' },
                    { value: 100, show: '100 miles' },
                    { value: 110, show: '110 miles' },
                    { value: 120, show: '120 miles' },
                    { value: 130, show: '130 miles' },
                    { value: 140, show: '140 miles' },
                    { value: 150, show: '150 miles' },
                    { value: 160, show: '160 miles' },
                    { value: 170, show: '170 miles' },
                    { value: 180, show: '180 miles' },
                    { value: 190, show: '190 miles' },
                    { value: 200, show: '200 miles' },
                ],
                isNoDefaultOptionValue: true,
                onChange: handleRadiusChange,
            },
        ],
    }

    return (
        <CustomDialog
            className="askQuesModal"
            function={props.handleLocation}
            open={props.openLocation}
            title={<div className="askingQ">Select location</div>}
        >
            <p className="paraDes">
                Search your location and select the maximum preferred pickup distance to see nearby
                listings.
            </p>
            <div className="row">{mapData(locationSelectorFields)}</div>
            <a onClick={() => resetMiles()} target="_blank" rel="noreferrer">
                <span className="terms">Reset Miles</span>
            </a>
            <GoogleMap
                latitude={formik.values.latitude}
                longitude={formik.values.longitude}
                locationName={formik.values.address1}
                radius={formik.values.radius}
                zoomLevel={11}
                fullscreenControl={false}
                width="100%"
                height="300px"
                disableControl={true}
            />
            <PrimaryButton
                id="apply-map"
                variant="contained"
                fullWidth
                btnSize="askQuesBtn mt-3"
                onClick={formik.handleSubmit}
            >
                Apply
            </PrimaryButton>
        </CustomDialog>
    )
}

export default LocationSelector
